import {
  logPreviousChunkFailure,
  recordChunkFailure,
  reportChunkFailure,
} from './chunkDiagnostics';

// Track failed chunks to avoid endless reload loops
const FAILED_CHUNKS_KEY = 'vite-failed-chunks';
let failedChunks = {};

// Extract the chunk URL from an error message
function extractChunkUrl(message) {
  const match = message.match(/https:\/\/[^'"\s]+/);
  return match ? match[0] : '';
}

// Detect whether an error originates from a failed dynamic import()
function isDynamicImportError(error) {
  if (!error || typeof error.message !== 'string') return false;
  const msg = error.message;
  return (
    msg.includes('dynamically imported module') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    error.name === 'ChunkLoadError'
  );
}

// Core handler for a chunk that has failed to load. Used by both the
// vite:preloadError listener and the unhandledrejection listener.
function handleChunkFailure(error, chunkUrl) {
  const now = Date.now();
  const BACKOFF_TIME = 30000; // 30 seconds

  // Diagnostics only: never alters the recovery logic below. The attempt is the
  // number of failures for this chunk inside the current backoff window.
  const previous = failedChunks[chunkUrl];
  const attempt =
    previous && now - previous.timestamp < BACKOFF_TIME
      ? previous.count + 1
      : 1;
  const record = recordChunkFailure(error, chunkUrl || null, attempt);
  if (record) reportChunkFailure(record);

  if (
    chunkUrl &&
    failedChunks[chunkUrl] &&
    now - failedChunks[chunkUrl].timestamp < BACKOFF_TIME
  ) {
    failedChunks[chunkUrl].count++;

    // If we've tried multiple times recently, wait for deployment to finish
    if (failedChunks[chunkUrl].count > 2) {
      console.info(
        'Multiple chunk loading failures detected. Likely during deployment. Waiting before retry...',
      );

      // Create a deployment-in-progress notification
      const deploymentNotice = document.createElement('div');
      deploymentNotice.style.position = 'fixed';
      deploymentNotice.style.top = '0';
      deploymentNotice.style.left = '0';
      deploymentNotice.style.right = '0';
      deploymentNotice.style.padding = '10px';
      deploymentNotice.style.backgroundColor = '#fcf8e3';
      deploymentNotice.style.color = '#8a6d3b';
      deploymentNotice.style.textAlign = 'center';
      deploymentNotice.style.zIndex = '9999';
      deploymentNotice.innerHTML = `
        <p>Deployment in progress. Application will refresh automatically when ready.
        <button id="force-refresh">Refresh Now</button></p>
      `;

      document.body.appendChild(deploymentNotice);

      document
        .getElementById('force-refresh')
        .addEventListener('click', () => {
          sessionStorage.removeItem(FAILED_CHUNKS_KEY);
          window.location.href = `/?force_refresh=${Date.now()}`;
        });

      // Set up a periodic check to see if deployment is complete
      const checkInterval = setInterval(() => {
        // Use a fetch with cache busting to check for the specific chunk
        fetch(`${chunkUrl}?check=${Date.now()}`, { cache: 'no-store' })
          .then((response) => {
            if (response.ok) {
              clearInterval(checkInterval);
              console.info('Chunk now available. Reloading application...');
              sessionStorage.removeItem(FAILED_CHUNKS_KEY);
              window.location.reload();
            }
          })
          .catch(() => {
            // Still not available, continue waiting
          });
      }, 5000); // Check every 5 seconds

      return;
    }
  } else {
    // First time seeing this chunk fail or it's been a while
    failedChunks[chunkUrl] = {
      timestamp: now,
      count: 1,
    };
  }

  // Save failed chunks to sessionStorage
  try {
    sessionStorage.setItem(FAILED_CHUNKS_KEY, JSON.stringify(failedChunks));
  } catch (e) {
    console.warn('Could not save to sessionStorage:', e);
  }

  // Log for the user
  console.info('Reloading page to recover from chunk loading error...');

  // Reload the page after a short delay with cache busting
  setTimeout(() => {
    window.location.href = `${window.location.pathname}?reload=${Date.now()}`;
  }, 1000);
}

// Resets in-memory state between tests. Not for production use.
export function _resetChunkStateForTesting(initialState = {}) {
  failedChunks = initialState;
}

// Initialize the chunk error handler
export function initChunkErrorHandler() {
  // Re-log the failure recorded before the last recovery reload; breadcrumbs
  // are in-memory and do not survive it.
  logPreviousChunkFailure();

  // Try to load any previously failed chunks from sessionStorage
  try {
    const stored = sessionStorage.getItem(FAILED_CHUNKS_KEY);
    if (stored) {
      failedChunks = JSON.parse(stored);
    }
  } catch (e) {
    console.warn('Could not access sessionStorage:', e);
  }

  // Register global Vite chunk error handler
  window.addEventListener('vite:preloadError', (event) => {
    const error = event.payload;
    console.warn('Failed to load chunk:', error);

    // Prevent the error from propagating
    event.preventDefault();

    const chunkUrl = error?.message ? extractChunkUrl(error.message) : '';
    handleChunkFailure(error, chunkUrl);
  });

  // Also catch dynamic import() failures that don't fire vite:preloadError.
  // In some browser/CDN combinations, import() may fail via an unhandled
  // rejection rather than the vite:preloadError event (e.g. Firefox, Safari
  // on certain CDN error codes).
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    if (!isDynamicImportError(error)) return;

    // Prevent the unhandled rejection from surfacing as a console error
    event.preventDefault();

    console.warn('Caught unhandled chunk load rejection:', error?.message);

    const chunkUrl = error?.message ? extractChunkUrl(error.message) : '';
    handleChunkFailure(error, chunkUrl);
  });
}
